/**
 * What the screener's header marks need from the app — `docs/plans/screener-bands.md`.
 *
 * - **Is the market live now?** 09:30-16:00 ET, or 04:00-20:00 ET when the
 *   screen's Pre/post tick is on; weekdays that are not closures. The grid
 *   draws the yellow and blue header lines only then.
 * - **Which category is an indicator in?** From the DSL catalog, for a computed
 *   column's default band.
 *
 * `App` owns the calendar and the catalog; the screener sits two components
 * below it. Module scope with subscribers — `symbol-index.ts`'s shape — rather
 * than two props threaded through `SectionGrid` for one consumer.
 */

import type { DslCatalog } from "@qufolio/screener-grid";

/** Minutes after midnight in New York, and the date there as `yyyy-mm-dd`. */
function newYork(now: Date): { minutes: number; date: string; weekday: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    weekday: "short",
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return {
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
    date: `${get("year")}-${get("month")}-${get("day")}`,
    weekday,
  };
}

/** Whether the US market is live at `now` for the header marks. */
export function liveWindow(now: Date, extendedHours: boolean, holidays: readonly string[] = []): boolean {
  const { minutes, date, weekday } = newYork(now);
  if (weekday === 0 || weekday === 6 || holidays.includes(date)) return false;
  const [open, close] = extendedHours ? [4 * 60, 20 * 60] : [9 * 60 + 30, 16 * 60];
  return minutes >= open && minutes < close;
}

// ── the store ────────────────────────────────────────────────────────────────

interface Marks {
  holidays: readonly string[];
  catalog: DslCatalog | undefined;
}

let marks: Marks = { holidays: [], catalog: undefined };
const listeners = new Set<() => void>();

/** `App` publishes what it has; either half may be left as it was. */
export function publishMarks(next: Partial<Marks>): void {
  const merged = { ...marks, ...next };
  if (merged.holidays === marks.holidays && merged.catalog === marks.catalog) return;
  marks = merged;
  for (const l of listeners) l();
}

export function readMarks(): Marks {
  return marks;
}

export function subscribeMarks(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
